// Libraries
import Helmet from "react-helmet";
import { Flex, Text as TextBase } from "rebass";
import { withRouter } from "next/router";
import { gql } from "@apollo/client";
import client from "../../../lib/apollo-client.js";

// Config
import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();
const { SITE_NAME } = publicRuntimeConfig;

// Component Imports
import {
	LayoutPadding,
	BannerStripe,
	Jumbotron,
	Layout,
	Text,
	BackButton,
	Paragraph,
	DonationTracker,
	Loading,
	SplitContent,
	FullWidthContent,
	ParagraphSection,
	RenderStreamField,
	ButtonLink,
} from "../../../components";
import { colors, layoutConfig } from "../../../styleguide/theme";

const HeroSection = ({
	jumbotronTitle,
	jumbotronSubtitle,
	jumbotronParagraph,
	jumbotronBackButtonTitle,
	jumbotronImage,
}) => (
	<Jumbotron image={jumbotronImage.rendition.url}>
		<BannerStripe bg={"transparent"} color={colors.white}>
			<Flex
				width={"100%"}
				flexDirection={["column", "column", "column", "row"]}
			>
				<Flex
					width={["100%", "100%", "100%", "35%"]}
					justifyContent={["center", "center", "center", "flex-end"]}
					alignItems={"center"}
					backgroundColor={colors.mediumblue2}
				>
					<LayoutPadding py={6}>
						<Flex
							justifyContent={["center", "center", "flex-start"]}
						>
							<BackButton
								title={jumbotronBackButtonTitle}
								href={"/campaigns/see-all"}
							/>
						</Flex>
					</LayoutPadding>
				</Flex>
				<Flex
					width={["100%", "100%", "100%", "65%"]}
					flexDirection={["column"]}
					backgroundColor={colors.lightblue}
					px={layoutConfig.paddingX}
					py={[6]}
				>
					{jumbotronTitle && (
						<Text as="h1" mb={[4]}>
							{jumbotronTitle}
						</Text>
					)}
					{jumbotronSubtitle && (
						<Text as="h3" pb={[2]}>
							{jumbotronSubtitle}
						</Text>
					)}
					{jumbotronParagraph && (
						<TextBase
							as="div"
							fontSize={0}
							fontWeight={100}
							css={`
								line-height: 22px;
							`}
							dangerouslySetInnerHTML={{
								__html: jumbotronParagraph,
							}}
						/>
					)}
				</Flex>
			</Flex>
		</BannerStripe>
	</Jumbotron>
);

const GET_SINGLE_CAMPAIGN = gql`
	query getSingleCampaign($id: String!) {
		campaign(id: $id) {
			nextPage {
				id
				jumbotronTitle
			}
			previousPage {
				id
				jumbotronTitle
			}
			body {
				... on DefaultStreamBlock {
					blockType
					value
				}
				... on SectionBlockOne {
					blockType
					title
					paragraph
					backgroundColor
					image {
						rendition(min: "1840x1083") {
							url
						}
					}
				}
				... on SectionBlockTwo {
					blockType
					title
					paragraph
					backgroundColor
					image {
						rendition(min: "1840x1083") {
							url
						}
					}
				}
				... on SectionBlockThree {
					blockType
					title
					paragraph
					backgroundColor
					image {
						rendition(min: "1840x1083") {
							url
						}
					}
				}
				... on SectionBlockFour {
					blockType
					title
					paragraph
					backgroundColor
					image {
						rendition(min: "1840x1083") {
							url
						}
					}
				}
				... on SectionBlockFive {
					blockType
					title
					subTitle
					paragraph
					paragraph2
				}
				... on SectionBlockSix {
					blockType
					paragraph
				}
				... on SectionBlockSeven {
					blockType
					title
					paragraph
					image {
						rendition(min: "1840x1083") {
							url
						}
					}
					cta
					ctaLink
				}
				... on SectionBlockEight {
					backgroundColor
					title
					blockType
					paragraph
					cta
					ctaLink
					detailsDonationsGoalAmount
					detailsDonationsRaisedAmount
					detailsDonationsCount
				}
				... on SectionBlockNine {
					backgroundColor
					title
					blockType
					paragraph
					cta
					ctaLink
					detailsDonationsGoalAmount
					detailsDonationsRaisedAmount
					detailsDonationsCount
				}
			}
			jumbotronImage {
				rendition(format: "jpeg", min: "1840x1083") {
					url
				}
			}
			jumbotronTitle
			jumbotronSubtitle
			jumbotronParagraph
			jumbotronBackButtonTitle
			section1Paragraph
			section1Quote
		}
	}
`;

export default withRouter(({ error, data, variables, id }) => {
	if (error) {
		return <Error statucCode={error.message} />;
	}
	const {
		jumbotronImage,
		jumbotronTitle,
		jumbotronSubtitle,
		jumbotronParagraph,
		jumbotronBackButtonTitle,
		section1Paragraph,
		section1Quote,
		nextPage,
		previousPage,
		body,
	} = data.campaign;

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Campaigns | ${SITE_NAME}`}</title>
			</Helmet>
			<Layout>
				<HeroSection
					{...{
						jumbotronImage,
						jumbotronTitle,
						jumbotronSubtitle,
						jumbotronParagraph,
						jumbotronBackButtonTitle,
					}}
				/>
				<ParagraphSection
					showNavigation
					content={section1Paragraph}
					quote={section1Quote}
					nextPage={nextPage || null}
					previousPage={previousPage || null}
				/>
				{/* We'll put streamfield here */}
				{/* <p>start</p> */}
				<RenderStreamField body={body} />
				{/* <RenderTemplate
									{...{
										campaignTemplateType,
										detailsMedia1,
										detailsMedia2,
										detailsDonationsGoalAmount,
										detailsDonationsRaisedAmount,
										detailsDonationsCount,
										detailsCtaTitle,
										detailsCtaUrl,
										section2Title,
										section2Paragraph1,
										section2Paragraph2,
										section3Title,
										section3Paragraph1,
										section4Title,
										section4Paragraph1,
										section5Title,
										section5Paragraph1
									}}
								/> */}
				<ButtonLink
					mx={"auto"}
					my={10}
					py={4}
					width={200}
					variant="donate"
					href={{
						pathname: `/donate/campaign`,
						query: { campaign: id },
					}}
				>
					Donate Now
				</ButtonLink>
			</Layout>
		</React.Fragment>
	);
});

export async function getServerSideProps({ query }) {
	const { id } = query;

	const variables = {
		id,
	};

	const { data, error } = await client.query({
		query: GET_SINGLE_CAMPAIGN,
		variables,
	});

	return {
		props: {
			data: error ? {} : data,
			error: error ? true : false,
			variables,
			id,
		},
	};
}
